import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { ChevronDown, ChevronsLeft, ChevronsRight, Files, ListX, PanelTopClose, RotateCw, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { findNavLabelByPath } from '../config/navigation';
import { FloatingMenu } from './FloatingMenu';
import './WorkspaceTabs.css';
import { MarketingViewsProvider } from './MarketingViewsProvider';

type WorkspaceTab = {
  key: string;
  path: string;
  title: string;
};

type WorkspacePane = {
  tab: WorkspaceTab;
  content: ReactNode;
  revision: number;
};

type TabContextMenu = {
  tab: WorkspaceTab;
  anchorEl: HTMLElement;
};

type WorkspaceTabsContextValue = {
  tabs: WorkspaceTab[];
  panes: Record<string, WorkspacePane>;
  currentPath: string;
  currentOutlet: ReactNode;
  contextMenu: TabContextMenu | null;
  setContextMenu: (menu: TabContextMenu | null) => void;
  openTab: (tab: WorkspaceTab) => void;
  closeTab: (key: string) => void;
  closeOtherTabs: (tab: WorkspaceTab) => void;
  closeAllTabs: () => void;
  refreshTab: (tab: WorkspaceTab) => void;
};

const WorkspaceTabsContext = createContext<WorkspaceTabsContextValue | null>(null);
const STORAGE_KEY = 'omni-growth:workspace-tabs';
const FALLBACK_PATH = '/dashboard';
// 暂时隐藏的入口不恢复为标签，页面文件保留供后续重新开放。
const HIDDEN_TAB_PATHS = new Set(['/', '/local-agent', '/local-agent/downloads']);
function isHiddenTabPath(path: string): boolean {
  return HIDDEN_TAB_PATHS.has(path.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/');
}

function tabPath(pathname: string, search: string): string {
  return `${pathname}${search}`;
}

function tabTitle(pathname: string): string {
  return findNavLabelByPath(pathname) ?? (pathname === '/' ? '首页' : '未命名页面');
}

function readSavedTabs(): WorkspaceTab[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const saved = JSON.parse(raw) as unknown;
    if (!Array.isArray(saved)) return [];
    return saved.flatMap((item): WorkspaceTab[] => {
      if (!item || typeof item !== 'object') return [];
      const candidate = item as Partial<WorkspaceTab>;
      if (typeof candidate.key !== 'string' || typeof candidate.path !== 'string' || typeof candidate.title !== 'string') return [];
      if (isHiddenTabPath(candidate.path) || isHiddenTabPath(candidate.key)) return [];
      return [{ key: candidate.key, path: candidate.path, title: candidate.title }];
    });
  } catch {
    return [];
  }
}

export function WorkspaceTabsProvider({ enabled, outlet, children }: {
  enabled: boolean;
  outlet: ReactNode;
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = tabPath(location.pathname, location.search);
  const [tabs, setTabs] = useState<WorkspaceTab[]>(readSavedTabs);
  const [panes, setPanes] = useState<Record<string, WorkspacePane>>({});
  const [contextMenu, setContextMenu] = useState<TabContextMenu | null>(null);

  useLayoutEffect(() => {
    if (!enabled || isHiddenTabPath(location.pathname)) return;
    const currentTab: WorkspaceTab = {
      key: currentPath,
      path: currentPath,
      title: tabTitle(location.pathname),
    };
    setTabs(previous => {
      const existing = previous.find(tab => tab.key === currentTab.key);
      if (existing?.title === currentTab.title) return previous;
      if (existing) return previous.map(tab => tab.key === currentTab.key ? currentTab : tab);
      return [...previous, currentTab];
    });
    setPanes(previous => previous[currentTab.key]
      ? previous
      : { ...previous, [currentTab.key]: { tab: currentTab, content: outlet, revision: 0 } });
  }, [currentPath, enabled, location.pathname, outlet]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
    } catch {
      // 无痕模式或浏览器策略禁止存储时，标签栏仍在当前会话中可用。
    }
  }, [tabs]);

  const openTab = useCallback((tab: WorkspaceTab) => {
    if (tab.key !== currentPath) navigate(tab.path);
  }, [currentPath, navigate]);

  const closeTab = useCallback((key: string) => {
    const closingIndex = tabs.findIndex(tab => tab.key === key);
    if (closingIndex < 0) return;
    const nextTabs = tabs.filter(tab => tab.key !== key);
    if (key === currentPath && nextTabs.length === 0 && currentPath === FALLBACK_PATH) return;

    setTabs(nextTabs);
    setPanes(previous => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setContextMenu(null);

    if (key !== currentPath) return;
    const nextActive = nextTabs[closingIndex] ?? nextTabs[closingIndex - 1];
    navigate(nextActive?.path ?? FALLBACK_PATH);
  }, [currentPath, navigate, tabs]);

  const closeOtherTabs = useCallback((tab: WorkspaceTab) => {
    setTabs([tab]);
    setPanes(previous => previous[tab.key] ? { [tab.key]: previous[tab.key] } : {});
    setContextMenu(null);
    navigate(tab.path);
  }, [navigate]);

  const closeAllTabs = useCallback(() => {
    if (currentPath === FALLBACK_PATH) {
      const fallbackTab: WorkspaceTab = { key: currentPath, path: currentPath, title: tabTitle(location.pathname) };
      setTabs([fallbackTab]);
      setPanes({ [fallbackTab.key]: { tab: fallbackTab, content: outlet, revision: 0 } });
    } else {
      setTabs([]);
      setPanes({});
      navigate(FALLBACK_PATH);
    }
    setContextMenu(null);
  }, [currentPath, location.pathname, navigate, outlet]);

  const refreshTab = useCallback((tab: WorkspaceTab) => {
    setContextMenu(null);
    if (tab.key !== currentPath) {
      setPanes(previous => {
        const next = { ...previous };
        delete next[tab.key];
        return next;
      });
      navigate(tab.path);
      return;
    }
    setPanes(previous => ({
      ...previous,
      [tab.key]: {
        tab,
        content: outlet,
        revision: (previous[tab.key]?.revision ?? 0) + 1,
      },
    }));
  }, [currentPath, navigate, outlet]);

  return (
    <WorkspaceTabsContext.Provider value={{
      tabs,
      panes,
      currentPath,
      currentOutlet: outlet,
      contextMenu,
      setContextMenu,
      openTab,
      closeTab,
      closeOtherTabs,
      closeAllTabs,
      refreshTab,
    }}>
      {children}
    </WorkspaceTabsContext.Provider>
  );
}

function useWorkspaceTabs() {
  const context = useContext(WorkspaceTabsContext);
  if (!context) throw new Error('WorkspaceTabs must be used inside WorkspaceTabsProvider');
  return context;
}

export function WorkspaceTabs() {
  const { tabs, currentPath, contextMenu, setContextMenu, openTab, closeTab, closeOtherTabs, closeAllTabs, refreshTab } = useWorkspaceTabs();
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLButtonElement>(null);
  const [visibleTabKeys, setVisibleTabKeys] = useState<string[]>([]);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);

  useLayoutEffect(() => {
    const element = tabsContainerRef.current;
    if (!element) return;

    const updateWidth = () => {
      setLayoutWidth(Math.round(element.clientWidth));
    };
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const container = tabsContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || tabs.length === 0) return;

    if (collapsed) {
      setVisibleTabKeys(previous => previous.length === 1 && previous[0] === currentPath ? previous : [currentPath]);
      return;
    }

    const itemWidths = new Map(
      Array.from(measure.querySelectorAll<HTMLElement>('[data-tab-key]')).map(item => [
        item.dataset.tabKey ?? '',
        item.getBoundingClientRect().width,
      ]),
    );
    const containerStyle = window.getComputedStyle(container);
    const measureStyle = window.getComputedStyle(measure);
    const availableWidth = Math.max(
      0,
      container.clientWidth
        - Number.parseFloat(containerStyle.paddingLeft)
        - Number.parseFloat(containerStyle.paddingRight),
    );
    const gap = Number.parseFloat(measureStyle.columnGap) || 0;
    const activeIndex = Math.max(0, tabs.findIndex(tab => tab.key === currentPath));
    let start = activeIndex;
    let end = activeIndex;
    let usedWidth = itemWidths.get(tabs[activeIndex].key) ?? 0;

    while (start > 0) {
      const nextWidth = itemWidths.get(tabs[start - 1].key) ?? 0;
      if (usedWidth + gap + nextWidth > availableWidth) break;
      start -= 1;
      usedWidth += gap + nextWidth;
    }
    while (end < tabs.length - 1) {
      const nextWidth = itemWidths.get(tabs[end + 1].key) ?? 0;
      if (usedWidth + gap + nextWidth > availableWidth) break;
      end += 1;
      usedWidth += gap + nextWidth;
    }

    const nextKeys = tabs.slice(start, end + 1).map(tab => tab.key);
    setVisibleTabKeys(previous => previous.length === nextKeys.length && previous.every((key, index) => key === nextKeys[index])
      ? previous
      : nextKeys);
  }, [collapsed, currentPath, layoutWidth, tabs]);

  const visibleTabs = useMemo(() => {
    const keys = collapsed ? [currentPath] : visibleTabKeys;
    return tabs.filter(tab => keys.includes(tab.key));
  }, [collapsed, currentPath, tabs, visibleTabKeys]);

  const hasHiddenTabs = visibleTabs.length < tabs.length;
  const canSelectTab = collapsed || hasHiddenTabs;
  const activeTab = tabs.find(tab => tab.key === currentPath);
  const cannotCloseCurrentTab = !activeTab || (tabs.length === 1 && currentPath === FALLBACK_PATH);

  const handleContextMenu = (event: ReactMouseEvent<HTMLDivElement>, tab: WorkspaceTab) => {
    event.preventDefault();
    setContextMenu({ tab, anchorEl: event.currentTarget });
  };

  return (
    <div className="workspace-tabs-bar" aria-label="已打开页面">
      <div ref={measureRef} className="workspace-tabs-measure" aria-hidden="true">
        {tabs.map(tab => (
          <div key={tab.key} data-tab-key={tab.key} className="workspace-tab">
            <span className="workspace-tab-title">{tab.title}</span>
            <span className="workspace-tab-close"><X size={13} /></span>
          </div>
        ))}
      </div>
      <div ref={tabsContainerRef} className="workspace-tabs" role="tablist" aria-label="工作台页面标签">
        {visibleTabs.map(tab => {
          const active = tab.key === currentPath;
          return (
            <div
              key={tab.key}
              className={`workspace-tab${active ? ' active' : ''}`}
              role="tab"
              aria-selected={active}
              tabIndex={0}
              title={tab.title}
              onClick={() => openTab(tab)}
              onContextMenu={event => handleContextMenu(event, tab)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openTab(tab);
                }
              }}
            >
              <span className="workspace-tab-title">{tab.title}</span>
              <button
                type="button"
                className="workspace-tab-close"
                aria-label={`关闭${tab.title}`}
                title={`关闭${tab.title}`}
                onClick={event => {
                  event.stopPropagation();
                  closeTab(tab.key);
                }}
              >
                <X size={13} />
              </button>
            </div>
          );
        })}
      </div>

      {tabs.length > 0 && (
        <div className="workspace-tab-controls">
          <button
            type="button"
            className="workspace-tab-control"
            title="刷新当前标签"
            aria-label="刷新当前标签"
            disabled={!activeTab}
            onClick={() => activeTab && refreshTab(activeTab)}
          >
            <RotateCw size={15} />
          </button>
          <button
            type="button"
            className="workspace-tab-control"
            title="关闭当前标签"
            aria-label="关闭当前标签"
            disabled={cannotCloseCurrentTab}
            onClick={() => activeTab && closeTab(activeTab.key)}
          >
            <X size={15} />
          </button>
          {tabs.length > 1 && <>
            <button
              type="button"
              className="workspace-tab-control workspace-tab-action"
              disabled={!activeTab}
              onClick={() => activeTab && closeOtherTabs(activeTab)}
            >
              关闭其他
            </button>
            <button
              type="button"
              className="workspace-tab-control workspace-tab-action workspace-tab-control-danger"
              onClick={closeAllTabs}
            >
              关闭全部
            </button>
            <button
              type="button"
              className="workspace-tab-control"
              title={collapsed ? '展开标签栏' : '收起标签栏'}
              aria-label={collapsed ? '展开标签栏' : '收起标签栏'}
              onClick={() => setCollapsed(value => !value)}
            >
              {collapsed ? <ChevronsRight size={15} /> : <ChevronsLeft size={15} />}
            </button>
            {canSelectTab && (
              <button
                ref={selectorRef}
                type="button"
                className="workspace-tab-control workspace-tab-selector-trigger"
                title="选择已打开页面"
                aria-label="选择已打开页面"
                aria-expanded={selectorOpen}
                onClick={() => setSelectorOpen(value => !value)}
              >
                <span>{collapsed ? '标签' : `更多 ${tabs.length - visibleTabs.length}`}</span>
                <ChevronDown size={14} />
              </button>
            )}
          </>}
        </div>
      )}

      <FloatingMenu
        anchorEl={selectorRef.current}
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        minWidth={188}
      >
        <div className="workspace-tab-selector-menu" role="menu" aria-label="选择已打开页面">
          {tabs.map(tab => {
            const active = tab.key === currentPath;
            return (
              <button
                key={tab.key}
                type="button"
                role="menuitem"
                className={active ? 'active' : ''}
                title={tab.title}
                onClick={() => {
                  openTab(tab);
                  setSelectorOpen(false);
                }}
              >
                <span>{tab.title}</span>
                {active && <span className="workspace-tab-selector-current">当前</span>}
              </button>
            );
          })}
        </div>
      </FloatingMenu>

      <FloatingMenu
        anchorEl={contextMenu?.anchorEl ?? null}
        open={Boolean(contextMenu)}
        onClose={() => setContextMenu(null)}
        minWidth={148}
      >
        <div className="workspace-tab-menu" role="menu" aria-label="标签操作">
          <button type="button" role="menuitem" onClick={() => contextMenu && refreshTab(contextMenu.tab)}><RotateCw size={14} />刷新</button>
          <button type="button" role="menuitem" onClick={() => contextMenu && closeTab(contextMenu.tab.key)}><PanelTopClose size={14} />关闭当前</button>
          <button type="button" role="menuitem" onClick={() => contextMenu && closeOtherTabs(contextMenu.tab)}><Files size={14} />关闭其他</button>
          <button type="button" role="menuitem" className="workspace-tab-menu-danger" onClick={closeAllTabs}><ListX size={14} />关闭全部</button>
        </div>
      </FloatingMenu>
    </div>
  );
}

export function WorkspaceTabOutlet() {
  const { panes, currentPath, currentOutlet } = useWorkspaceTabs();
  const hasCurrentPane = Boolean(panes[currentPath]);

  return (
    <>
      {Object.values(panes).map(pane => {
        const active = pane.tab.key === currentPath;
        return (
          <div key={`${pane.tab.key}:${pane.revision}`} className="workspace-tab-pane" hidden={!active} aria-hidden={!active}>
            <MarketingViewsProvider path={pane.tab.path} active={active}>{pane.content}</MarketingViewsProvider>
          </div>
        );
      })}
      {!hasCurrentPane && <div className="workspace-tab-pane"><MarketingViewsProvider path={currentPath} active>{currentOutlet}</MarketingViewsProvider></div>}
    </>
  );
}
